import { useEffect, useRef } from 'react';

/**
 * Modaler Dialog auf Basis des nativen `<dialog>`-Elements.
 *
 * Das Element bringt mit, was einen Dialog modal macht: Fokus bleibt im Dialog,
 * der Hintergrund ist für Maus und Vorlesesoftware inert, Escape schließt. Eine
 * Nachbildung mit `<div>` müsste all das von Hand richtig machen und tut es
 * erfahrungsgemäß nicht.
 */
interface ModalProps {
  isOpen: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  /** Aktionen am unteren Rand, typischerweise Abbrechen und Bestätigen. */
  actions: React.ReactNode;
}

export const Modal = ({
  isOpen,
  title,
  onClose,
  children,
  actions,
}: ModalProps): React.JSX.Element => {
  const dialogRef = useRef<HTMLDialogElement | null>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen && !dialog.open) dialog.showModal();
    if (!isOpen && dialog.open) dialog.close();
  }, [isOpen]);

  return (
    <dialog
      ref={dialogRef}
      className="modal"
      /* Escape und das Schließen über die Tastatur melden zurück an React. */
      onClose={onClose}
      /* Ein Klick auf den Hintergrund trifft das Dialogelement selbst. */
      onClick={(event) => {
        if (event.target === dialogRef.current) onClose();
      }}
    >
      <form method="dialog" className="modal__body" onSubmit={(event) => event.preventDefault()}>
        <h3 className="modal__title">{title}</h3>
        <div className="modal__content">{children}</div>
        <div className="modal__actions">{actions}</div>
      </form>
    </dialog>
  );
};
